import { getDomainGA4Analytics } from "@mp-commons/config/GA4Accounts.js";
import { NielsenAudicomMeasurementTrackingIds, NielsenVideoMeasurementTrackingIds } from "@mp-commons/config/NielsenAccounts.js";
import { getSecondLevelDomain } from "@mp-commons/lib/utils/DomainNameUtils.js";
import { getCPProductFromHostname, getYMHostByCPProduct } from "@mp-commons/config/CPProducts.js";
import { CP_EmbedType } from "@mp-commons/modules/ads/adcall/CustomParams.js";
import { ExternallyPassedContentAdData } from "@mp-commons/modules/ads/adcall/composable/ContentSpecificAdCallFilter.js";
import { NielsenCollectorFactory } from "@mp-commons/modules/stats/collectors/NielsenCollector.js";
import { AttachableEventMapper, MapperConfigFlags } from "@mp-commons/modules/stats/mappers/CommonEventMapper.js";
import { GA4Mapper } from "../../stats/mappers/GA4Mapper.js";
import { MegarideCSStreamingTagMapper } from "../../stats/mappers/MegarideCSStreamingTagMapper.js";
import { MegarideNielsenMapper } from "../../stats/mappers/MegarideNielsenMapper.js";
import { MegarideYMStatsMapper } from "../../stats/mappers/MegarideYMStatsMapper.js";
import { getGDPR } from "@mp-commons/modules/ads/GDPR.js";
import { MegarideAudicomMapper } from "../../stats/mappers/MegarideAudicomMapper.js";
import { AudicomConfig } from "@mp-commons/modules/stats/collectors/AudicomPairingPing.js";
import { generateUID } from "@mp-commons/lib/utils/Utility.js";

export interface GenericStatsProviderFlags extends MapperConfigFlags {
    enable: boolean
}


export interface StatsConfig  {

    ga4: GenericStatsProviderFlags,
    nielsen: {enable: boolean},
    comscore: GenericStatsProviderFlags,
    ymstats: GenericStatsProviderFlags,
    playerName: string,
}


/**
 * Builds an array of event mappers for analytics and tracking based on the provided configuration.
 *
 * This function creates and returns a list of `AttachableEventMapper` instances for various analytics providers
 * (GA4, Nielsen, YMStats, Comscore) depending on the enabled flags in the `StatsConfig` object.
 *
 * @param win - The browser window object, used for accessing location and other window-specific properties.
 * @param embedType - The type of embed for the player, used for analytics context.
 * @param config - The configuration object specifying which analytics providers are enabled and their settings.
 * @param externalData - Optional externally passed content/ad data for analytics mappers that require it.
 * @returns An array of initialized `AttachableEventMapper` instances for the enabled analytics providers.
 */
export function StatsMapperBuilder(win: Window,
                                    embedType: CP_EmbedType,
                                    config: StatsConfig,
                                    externalData?: ExternallyPassedContentAdData
                                    ): AttachableEventMapper[] {
    const product = getCPProductFromHostname(win.location.hostname);
    const seclev = getSecondLevelDomain(win.location.hostname);
    
    
                            

    const mappers: AttachableEventMapper[] = [];

    if(config.ga4.enable) {
        const ga4config = getDomainGA4Analytics(seclev);
        mappers.push(new GA4Mapper(win,embedType,{
            ...ga4config,
            ...config.ga4
        }));
    }

    if(config.nielsen.enable && product in NielsenVideoMeasurementTrackingIds) {
        const trackingIdMapper = NielsenVideoMeasurementTrackingIds[product];
        const nielsenfactory = new NielsenCollectorFactory(win,trackingIdMapper,config.playerName,getGDPR());
        mappers.push(new MegarideNielsenMapper(nielsenfactory));
    }
    if(config.ymstats.enable) {
        const ymHostname = getYMHostByCPProduct(win.location.hostname,seclev,product);
        mappers.push(new MegarideYMStatsMapper(win,{
            ...config.ymstats,
            embedType: embedType,
            playerName: config.playerName,
            ymHostname: ymHostname,
            product: product
        }))
    }


    // Video censuario Comscore: stesso SDK/publisher, in modalità Audicom per i prodotti
    // aderenti (config presente in NielsenAudicomMeasurementTrackingIds), altrimenti Comscore
    // "semplice". La modalità è decisa dalla config di prodotto, non da un flag dedicato.
    if (config.comscore.enable) {
        const comscoreConfig = {
            publisherId: "18904044",
            debug: true
        };

        if (product in NielsenAudicomMeasurementTrackingIds) {
            const appID = NielsenAudicomMeasurementTrackingIds[product].default;
            const audicomConfig: AudicomConfig = {
                appID: appID,
                sessionID: generateUID()
            };
            console.log("[AUDICOM] Audicom mode enabled, App ID:", appID);
            mappers.push(new MegarideAudicomMapper(win, {
                comscore: comscoreConfig,
                audicom: audicomConfig
            }, getGDPR(), externalData));
        } else {
            mappers.push(new MegarideCSStreamingTagMapper(win, comscoreConfig, getGDPR(), externalData));
        }
    }


    return mappers;
}
